import { useEffect, useState, useRef } from 'react';
import { Plus, ExternalLink, Save, Trash2, RefreshCcw, Upload, Image as ImageIcon } from 'lucide-react';
import { Quiz } from '../types';
import { getQuizzes, createQuiz, updateQuiz, deleteQuiz } from '../services/quizzesService';
import { uploadImage, generateImagePath } from '../services/imageUploadService';
import { useAuth } from '../contexts/AuthContext';

const emptyForm: Omit<Quiz, 'id'> & { id?: string } = {
  id: undefined,
  title: '',
  description: '',
  url: '',
  image: '',
  category: '',
  tags: [],
};

export default function QuizzesPage() {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [formData, setFormData] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadQuizzes = async () => {
    setIsLoading(true);
    const data = await getQuizzes();
    setQuizzes(data);
    setIsLoading(false);
  };

  useEffect(() => {
    loadQuizzes();
  }, []);

  const handleSelectQuiz = (quiz: Quiz) => {
    setFormData({
      id: quiz.id,
      title: quiz.title,
      description: quiz.description,
      url: quiz.url,
      image: quiz.image || '',
      category: quiz.category || '',
      tags: quiz.tags || [],
    });
  };

  const resetForm = () => {
    setFormData(emptyForm);
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    if (field === 'tags') {
      setFormData((prev) => ({
        ...prev,
        tags: value.split(',').map((tag) => tag.trim()).filter(Boolean),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.url.trim()) {
      alert('Titolo e URL del quiz sono obbligatori.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        url: formData.url.trim(),
        image: formData.image?.trim() || '',
        category: formData.category?.trim() || '',
        tags: formData.tags || [],
      };

      if (formData.id) {
        await updateQuiz(formData.id, payload);
      } else {
        await createQuiz(payload);
      }

      await loadQuizzes();
      resetForm();
    } catch (error) {
      console.error('Errore durante il salvataggio del quiz:', error);
      alert('Errore nel salvataggio del quiz. Controlla la console per maggiori dettagli.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (quizId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo quiz?')) return;
    try {
      await deleteQuiz(quizId);
      await loadQuizzes();
      if (formData.id === quizId) {
        resetForm();
      }
    } catch (error) {
      console.error('Errore durante l\'eliminazione del quiz:', error);
      alert('Impossibile eliminare il quiz. Riprova più tardi.');
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Verifica autenticazione
    if (!user) {
      alert('Devi essere autenticato per caricare immagini. Effettua il login.');
      return;
    }

    try {
      setIsUploading(true);
      const quizId = formData.id || `temp_${Date.now()}`;
      const imagePath = generateImagePath('quizzes', quizId, file.name);
      const imageUrl = await uploadImage(file, imagePath);
      setFormData(prev => ({ ...prev, image: imageUrl }));
      console.log('✅ Immagine quiz caricata:', imageUrl);
    } catch (error) {
      console.error('❌ Errore caricamento immagine:', error);
      alert('Errore durante il caricamento dell\'immagine. Assicurati di essere autenticato.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Quiz interattivi</h1>
              <p className="mt-2 text-gray-600">
                Gestisci i quiz e i giochi interattivi da mostrare nell'app mobile. Ogni quiz viene aperto in una WebView.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadQuizzes}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                disabled={isLoading}
              >
                <RefreshCcw size={16} className="mr-2" /> Aggiorna
              </button>
              <button
                onClick={resetForm}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus size={16} className="mr-2" /> Nuovo quiz
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {isLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Caricamento quiz...</p>
              </div>
            ) : quizzes.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun quiz disponibile</h3>
                <p className="text-gray-600">Usa il form sulla destra per aggiungere il primo quiz.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {quizzes.map((quiz) => (
                  <div
                    key={quiz.id}
                    className={`bg-white rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md cursor-pointer overflow-hidden ${
                      formData.id === quiz.id ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200'
                    }`}
                    onClick={() => handleSelectQuiz(quiz)}
                  >
                    {/* Preview immagine */}
                    {quiz.image && (
                      <div className="h-48 bg-gray-100 overflow-hidden">
                        <img
                          src={quiz.image}
                          alt={quiz.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    <div className="p-6">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{quiz.title}</h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (quiz.url) {
                                  window.open(quiz.url, '_blank', 'noopener noreferrer');
                                }
                              }}
                              className="text-indigo-600 hover:text-indigo-800 transition-colors"
                              title="Apri quiz"
                            >
                              <ExternalLink size={16} />
                            </button>
                          </div>
                          {quiz.category && (
                            <span className="inline-block mb-3 px-3 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                              {quiz.category}
                            </span>
                          )}
                          <p className="text-gray-600 mb-3">{quiz.description || 'Nessuna descrizione disponibile.'}</p>
                          {quiz.tags && quiz.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {quiz.tags.map((tag) => (
                                <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(quiz.id);
                          }}
                          className="text-red-600 hover:text-red-800 transition-colors p-1"
                          title="Elimina quiz"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {formData.id ? 'Modifica quiz' : 'Nuovo quiz'}
              </h2>
              <p className="text-sm text-gray-600">
                Inserisci titolo, descrizione e link al quiz esterno. Puoi aggiungere un'immagine di copertina opzionale.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titolo *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Es. Quiz sul territorio"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrizione</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Breve descrizione del quiz"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link del quiz *</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => handleChange('url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Inserisci l'URL completo del quiz (es. Google Forms, Typeform, ecc.). Verrà aperto in app tramite WebView.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Immagine di copertina</label>
                
                {/* Preview immagine */}
                {formData.image && (
                  <div className="mb-4">
                    <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={formData.image}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Upload button */}
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={isUploading}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        Caricamento...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        {formData.image ? 'Cambia immagine' : 'Carica immagine'}
                      </>
                    )}
                  </button>

                  {/* Input file nascosto */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isUploading}
                  />

                  {/* URL input come alternativa */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ImageIcon size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => handleChange('image', e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Oppure inserisci URL immagine..."
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Carica un'immagine dal computer o inserisci un URL. L'immagine verrà mostrata come copertina del quiz nell'app.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Es. Storia locale"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
                  <input
                    type="text"
                    value={(formData.tags || []).join(', ')}
                    onChange={(e) => handleChange('tags', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={16} className="mr-2" />
                {isSaving ? 'Salvataggio...' : 'Salva quiz'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
